import {
  Button,
  Empty,
  Space,
  Tooltip
} from 'antd';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import 'highcharts/modules/export-data';
import 'highcharts/modules/exporting';
import 'highcharts/modules/full-screen';
import 'highcharts/modules/offline-exporting';
import { v4 as uuid } from 'uuid';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, {
  useEffect, useRef, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { InfoCircleTwoTone } from '@ant-design/icons';
import allActions from '../../../../../actions';
import { getConvertedPrice } from '../../../../../utils/DateAndNumberFunctions';
import { NUMBER_OF_RECORDS_TO_FETCH } from '../../../../../utils/GlobalConstants';
import { widgetGroups, widgetViewVals } from '../../../../Config/adminSettings/widgetSetting/WidgetViewConstants';
import { formatLargeNumber } from '../../../../MaterialDetails/tabElements/TabComponents/tabGraph/TabGraphHelpers';
import styles from '../styles.module.scss';
import consumptionGroupID from '../../../../../images/consumptionGroupID.png';
import {
  colors, defaultColorCodes,
  defaultTypes, scales
} from '../utils/WidgetConstants';
import {
  getMax,
  getExcessStockMax,
  hasDataAfterFilter,
  getDashboardFilters,
  getFiltersForMatList,
  getSampleExcessStock,
  getSampleGroupOrSPOrCrit,
  getSampleOptInvStrategyData,
} from '../utils/WidgetFunctions';
import { widgetTypeKeys } from '../utils/widgetTypes';
import DraggableModal from '../../../../../components/draggableModal/DraggableModal';
import TextWidgetContainer from '../textWidget/TextWidgetContainer';

export default function VerticalGraphWidget({
  theme, inventoryStatusData, showLegendSetting, canViewValues, keysVisible,
  widgetID, getFormattedData, colorMapping,
  indexBy, textWidgetID,
}) {
  const isStackedChart = widgetID !== widgetTypeKeys.optimalInventoryByStrategy
    && widgetID !== widgetTypeKeys.optimalInventoryByROP;
  const selectedPlants = useSelector((state) => state.commonState.selectedPlants);
  const selectedCCs = useSelector((state) => state.commonState.selectedCompanyCodes);
  const userSettings = useSelector((state) => state.commonState.userSettings);
  const defaultCurrency = useSelector((state) => state.commonState?.userSettings?.DEFAULT_CURRENCY);
  const dashboardFilter = useSelector((state) => state.commonState.localDashboardFilters);
  const currencyList = useSelector((state) => state.commonState?.currencyList);
  const dragStatus = useSelector((state) => state.dashboardState.dragStatus);
  const widgetViews = useSelector((state) => state.commonState.widgetViews);
  const gridData = useSelector((state) => state.dashboardState.gridData);
  const dispatch = useDispatch();
  const [data, setData] = useState([]);
  const [maxValue, setMaxValue] = useState();
  const [scale, setScale] = useState();
  const history = useHistory();
  const [legend, setLegend] = useState(showLegendSetting ?? null);
  const [legendsList, setLegendsList] = useState([]);
  const [containerNode, setContainerNode] = useState(null);
  const [resolvedColors, setResolvedColors] = useState(null);
  const [infoTitle, setInfoTitle] = useState('');
  const [infoContent, setInfoContent] = useState(<></>);
  const [infoModalVisible, setInfoModalVisible] = useState(false);
  const [animation, setAnimation] = useState(false);
  const containerRef = React.useCallback((node) => {
    setContainerNode(node);
  }, []);

  const animationVal = {
    duration: 1200,
    easing: 'easeOutBounce',
    start: {
      y: 0
    }
  };

  const legendProps = {
    dataFrom: 'keys',
    anchor: 'left',
    direction: 'row',
    justify: false,
    itemDirection: 'left-to-right',
    symbolShape: 'square',
    effects: [
      {
        on: 'hover',
        style: {
          itemOpacity: 1,
        },
      },
    ],
  };

  useEffect(() => {
    setLegend(!!showLegendSetting);
  }, [showLegendSetting]);

  const getLabel = (item, color) => {
    switch (color) {
      case colors.GREEN:
        return item?.GREEN_RECORDS > 0 ? formatLargeNumber(item?.GREEN_RECORDS ?? 0) : '';
      case colors.RED:
        return item?.RED_RECORDS > 0 ? formatLargeNumber(item?.RED_RECORDS ?? 0) : '';
      case colors.YELLOW:
        return item?.YELLOW_RECORDS > 0 ? formatLargeNumber(item?.YELLOW_RECORDS ?? 0) : '';
      default: return '?';
    }
  };

  const getColor = (item, color) => {
    switch (color) {
      case colors.GREEN:
        return item?.GREEN_RECORDS > 0 ? color : 'rgb(0,0,0,0)';
      case colors.RED:
        return item?.RED_RECORDS > 0 ? color : 'rgb(0,0,0,0)';
      case colors.YELLOW:
        return item?.YELLOW_RECORDS > 0 ? color : 'rgb(0,0,0,0)';
      default: return '?';
    }
  };

  const getID = (color) => {
    if (widgetID === widgetTypeKeys.excessStockByStatus) {
      switch (color) {
        case colors.GREEN:
          return 'Green';
        case colors.RED:
          return 'Red';
        case colors.YELLOW:
          return 'Yellow';
        default: return undefined;
      }
    } else {
      switch (color) {
        case colors.GREEN:
          return defaultTypes.APPROVED;
        case colors.RED:
          return defaultTypes.NOT_APPROVED;
        case colors.YELLOW:
          return defaultTypes.UNKNOWN;
        default: return undefined;
      }
    }
  };

  const getLegend = () => {
    const colorLegend = [];
    if (data.some((d) => d.GREEN_RECORDS > 0)) {
      colorLegend.push({
        label: defaultTypes.APPROVED,
        color: defaultColorCodes.Green,
      });
    }
    if (data.some((d) => d.YELLOW_RECORDS > 0)) {
      colorLegend.push({
        label: defaultTypes.UNKNOWN,
        color: defaultColorCodes.Yellow,
      });
    }
    if (data.some((d) => d.RED_RECORDS > 0)) {
      colorLegend.push({
        label: defaultTypes.NOT_APPROVED,
        color: defaultColorCodes.Red,
      });
    }
    return colorLegend;
  };

  const getLegends = (key, color) => {
    const legends = [];
    data?.forEach((item) => {
      Object.keys(item).forEach((k) => {
        if (k === key) {
          legends.push({
            id: getID(color),
            label: getLabel(item, color),
            indexValue: indexBy ? item[indexBy] : item.GROUP,
            color: getColor(item, color),
          });
        }
      });
    });
    return legends;
  };

  const handleClick = (e, noStatusLight = false) => {
    if (e?.label === '' || dragStatus) return;
    const indexValue = e.key;
    const viewID = widgetViews?.find((x) => {
      const group = widgetGroups[widgetID]?.find(
        (grp) => grp.key === indexValue || grp[grp.key] === indexValue,
      );
      return x.WidgetID === widgetID
        && (x.GroupID === group?.key || x.GroupID === widgetViewVals.all);
    })?.ViewID;
    if (viewID) {
      dispatch(allActions.CommonActions.selectWidgetView(viewID));
    } else {
      dispatch(allActions.CommonActions.selectWidgetView(userSettings?.DEFAULT_USER_VIEW));
    }
    let searchObject = getFiltersForMatList(e, widgetID, colorMapping, noStatusLight);
    if (selectedPlants?.length > 0) {
      searchObject = {
        ...searchObject,
        PLANT_FACILITY_SAP_ID: [
          {
            ColumnName: 'PLANT_FACILITY_SAP_ID',
            FilterOperator: 'EqualTo',
            FilterValue:
              selectedPlants,
          },
        ],
      };
    } else if (selectedCCs?.length > 0) {
      searchObject = {
        ...searchObject,
        COMPANY_CODE: [
          {
            ColumnName: 'COMPANY_CODE',
            FilterOperator: 'EqualTo',
            FilterValue:
              selectedCCs,
          },
        ],
      };
    }
    if (searchObject?.CU_DELETED) {
      searchObject.MATERIAL_TYPE_DELETION_IND = [
        {
          ColumnName: 'MATERIAL_TYPE_DELETION_IND',
          FilterOperator: 'EqualTo',
          FilterValue: ['1'],
        },
      ];
    }
    searchObject = {
      ...searchObject,
      ...getDashboardFilters(dashboardFilter),
    };
    const materialViewState = {
      headerCells: [],
      searchFilters: searchObject,
      scrollPage: {
        currentNoOfRecords: 0,
        noOfRecordsToFetch: NUMBER_OF_RECORDS_TO_FETCH,
      },
    };
    if ((selectedPlants?.length > 0
      && !_.isEqual(selectedPlants?.sort(), userSettings?.DEFAULT_PLANTS?.sort()))
      || (selectedCCs?.length > 0
        && !_.isEqual(selectedCCs?.sort(), userSettings?.DEFAULT_COMPANY_CODES?.sort()))) {
      dispatch(allActions.CommonActions.setUseDefaultSettings(false));
    }
    dispatch(allActions.MaterialListActions.setLocalMaterialView(materialViewState));
    dispatch(allActions.MaterialListActions.setSelectedRowKeys([]));
    history.push('/material-list');
  };

  const getOptInvStrategyLegend = (item) => ({
    id: item.id,
    indexValue: item.id,
    label: item?.count > 0 ? formatLargeNumber(item?.count) : '',
    color: item.color,
  });

  const showInfoStorageCheckID = () => {
    setInfoTitle('Consumption Group');
    setInfoContent(
      <div
        onKeyPress={null}
        onClick={() => window.open(consumptionGroupID)}
        role="button"
        tabIndex={0}
      >
        <img style={{ cursor: 'pointer' }} title="Click to open image in new window" src={consumptionGroupID} alt="Status Tables" />
      </div>,
    );
    setInfoModalVisible(true);
  };

  const closeInfoModal = () => setInfoModalVisible(false);
  const modalTitle = <div className="handle">{infoTitle}</div>;

  useEffect(() => {
    let legends = [];
    if (keysVisible) {
      legends.push({
        ...legendProps,
        anchor: 'bottom',
        translateX: 0,
        data: getLegend(),
        translateY: legend ? 95 : 45,
        itemsSpacing: 60,
        itemWidth: 100,
        itemHeight: 20,
        itemOpacity: 0.85,
        symbolSize: legend ? 12 : 20,
        symbolShape: 'circle',
      });
    }
    if (legend) {
      if (widgetID === widgetTypeKeys.optimalInventoryByStrategy) {
        legends = [...legends, {
          ...legendProps,
          data: data?.map((item) => getOptInvStrategyLegend(item)),
          translateX: 0,
          translateY: 105,
          itemsSpacing: 10,
          itemWidth: 400 / (data?.length ?? 8),
          itemHeight: 20,
          itemOpacity: 0.85,
          symbolSize: 20,
          symbolShape: 'circle',
        }];
      } else {
        legends = [...legends, {
          ...legendProps,
          data: getLegends('GREEN_COLOR', colors.GREEN),
          translateX: keysVisible ? -35 : 100,
          translateY: keysVisible ? 70 : 80,
          itemsSpacing: keysVisible ? 15 : 10,
          itemWidth: 400 / (data?.length ?? keysVisible ? 8 : 3),
          itemHeight: keysVisible ? 12 : 20,
          itemOpacity: 0.85,
          symbolSize: 12,
        },
        {
          ...legendProps,
          data: getLegends('RED_COLOR', colors.RED),
          translateX: keysVisible ? -35 : 100,
          translateY: keysVisible ? 83 : 93,
          itemsSpacing: keysVisible ? 15 : 10,
          itemWidth: 400 / (data?.length ?? keysVisible ? 8 : 3),
          itemHeight: keysVisible ? 12 : 20,
          itemOpacity: 0.85,
          symbolSize: 12,
        },
        {
          ...legendProps,
          data: getLegends('YELLOW_COLOR', colors.YELLOW),
          translateX: keysVisible ? -35 : 100,
          translateY: keysVisible ? 96 : 106,
          itemsSpacing: keysVisible ? 15 : 10,
          itemWidth: 400 / (data?.length ?? keysVisible ? 8 : 3),
          itemHeight: 20,
          itemOpacity: 0.85,
          symbolSize: 12,
        }];
      }
    }

    setLegendsList(legends);
  }, [legend, keysVisible, data]);

  useEffect(() => {
    if (!inventoryStatusData
      || inventoryStatusData.length === 0
      || _.isEmpty(inventoryStatusData)) return;
    setScale(scales.linear);
    let tempMaxVal = null;
    let formattedData = getFormattedData(inventoryStatusData, dashboardFilter ?? null);
    if (widgetID === widgetTypeKeys.optimalInventoryByStrategy) {
      tempMaxVal = formattedData[0]?.value;
    } else if (widgetID === widgetTypeKeys.excessStockByStatus) {
      tempMaxVal = getExcessStockMax(formattedData?.[0]);
    } else {
      tempMaxVal = getMax(formattedData?.[0]);
    }

    if (tempMaxVal) {
      if (widgetID !== widgetTypeKeys.cleanUpWidget
        && widgetID !== widgetTypeKeys.inflowWatchlist) {
        tempMaxVal /= 10;
        tempMaxVal = (Math.ceil(tempMaxVal) * 10) + 10;
      } else {
        tempMaxVal += (tempMaxVal * 0.1);
      }
      setMaxValue(tempMaxVal);
    }

    if (!formattedData.length) {
      const ids = [
        widgetTypeKeys.cleanUpWidget,
        widgetTypeKeys.inflowWatchlist,
        widgetTypeKeys.excessStockByStatus,
        widgetTypeKeys.optimalInventoryByCrit,
        widgetTypeKeys.optimalInventoryBySP,
        widgetTypeKeys.optimalInventoryByROP,
        widgetTypeKeys.optimalInventoryByGroup,
      ];
      if (widgetID === widgetTypeKeys.optimalInventoryByStrategy) {
        formattedData = getSampleOptInvStrategyData();
      } else if (widgetID === widgetTypeKeys.excessStockByStatus) {
        formattedData = getSampleExcessStock();
      } else if (ids.includes(widgetID)) {
        formattedData = getSampleGroupOrSPOrCrit(widgetID, indexBy);
      }
      setMaxValue(1);
    }
    setData(formattedData);
  }, [inventoryStatusData, dashboardFilter]);

  const updateValueScale = () => {
    const newScale = scale.type === scales.symlog.type ? scales.linear : scales.symlog;
    setScale(newScale);
  };

  const toggleLegend = () => {
    setLegend((prev) => !prev);
  };

  const getROPDeviation = (v) => {
    if (!v.data) return 0;
    if (Number.isNaN(v.data.deviation)) return v.data.deviation;
    return v.data.deviation?.toFixed(2);
  };

  const getInventoryValue = (v) => {
    if (!widgetID) return null;
    const originalVal = v.data?.inventory;
    let convertedVal = originalVal;
    let curr = 'NOK';
    if (defaultCurrency && defaultCurrency !== 'NOK') {
      const temp = currencyList?.find((x) => defaultCurrency === x?.FROM_CURRENCY);
      convertedVal = getConvertedPrice(
        originalVal, temp?.FROM_RATIO, temp?.TO_RATIO, temp?.EXCHANGE_RATE,
      );
      curr = defaultCurrency;
    }
    return `${formatLargeNumber(convertedVal)} ${curr}`;
  };

  const getNoOfRecords = (v) => {
    if (!widgetID) return null;
    return v.data.count;
  };

  const getLegendLabel = () => {
    if (widgetID !== widgetTypeKeys.cleanUpWidget && widgetID !== widgetTypeKeys.inflowWatchlist) return 'Percentage';
    return 'Number of records';
  };

  const getBarLabel = (v) => {
    if (widgetID === widgetTypeKeys.cleanUpWidget
      || widgetID === widgetTypeKeys.inflowWatchlist) return v.value;
    return `${v.value.toFixed(1)}%`;
  };

  const getAxisLeftFormat = (v) => {
    if (widgetID === widgetTypeKeys.cleanUpWidget
      || widgetID === widgetTypeKeys.inflowWatchlist) return v;

    // Need to change this
    return `${v}%`;
  };

  const chartComponentRef = useRef(null);
  const [chartHeight, setChartHeight] = useState(null);
  const [legendsHeight, setLegendsHeight] = useState('100%');

  useEffect(() => {
    if (containerNode) {
      const computedStyle = getComputedStyle(containerNode);
      setResolvedColors({
        neutral100: computedStyle.getPropertyValue('--neutral--100').trim(),
        neutral400: computedStyle.getPropertyValue('--neutral--400').trim(),
        neutral600: computedStyle.getPropertyValue('--neutral--600').trim(),
        neutral800: computedStyle.getPropertyValue('--neutral--800').trim(),
        widgetBg: computedStyle.getPropertyValue('--widget-background').trim(),
        [colors.RED]: computedStyle.getPropertyValue('--red-color').trim(),
        [colors.GREEN]: computedStyle.getPropertyValue('--green-color').trim(),
        [colors.YELLOW]: computedStyle.getPropertyValue('--yellow-color').trim(),
      });
    }
  }, [containerNode]);

  const getChartHeight = () => {
    if (widgetID === widgetTypeKeys.optimalInventoryByStrategy
      || widgetID === widgetTypeKeys.excessStockByStatus) {
      return 12;
    }
    return 30;
  };

  useEffect(() => {
    if (!containerNode) return;
    const updateDimensions = () => {
      if (!document.fullscreenElement) {
        const width = containerNode.offsetWidth;
        if (!gridData) {
          setChartHeight(((width * 0.33) + 15) - (legend ? 60 : getChartHeight()));
          return;
        }
        const totalHeight = gridData?.gridHeight;
        let finalHeight = totalHeight;
        const chartEl = containerNode?.getBoundingClientRect();
        const chartWidth = (chartEl?.right ?? 0) - (chartEl?.left ?? 0);
        switch (gridData?.colCount) {
          case 3:
            finalHeight = (totalHeight / 3) - 55;
            break;
          case 2:
            finalHeight = 0.6 * chartWidth;
            break;
          default:
            finalHeight = 0.7 * chartWidth;
        }
        if (legend) {
          let tempLegendHeight = '100%';
          switch (widgetID) {
            case widgetTypeKeys.optimalInventoryByGroup:
            case widgetTypeKeys.cleanUpWidget:
            case widgetTypeKeys.inflowWatchlist:
              tempLegendHeight = (0.25 * finalHeight) - 5;
              finalHeight -= (0.25 * finalHeight);
              break;
            case widgetTypeKeys.optimalInventoryByCrit:
              tempLegendHeight = (0.35 * finalHeight) - 5;
              finalHeight -= (0.35 * finalHeight);
              break;
            case widgetTypeKeys.optimalInventoryByStrategy:
              tempLegendHeight = (0.15 * finalHeight) - 5;
              finalHeight -= (0.15 * finalHeight);
              break;
            case widgetTypeKeys.excessStockByStatus:
            case widgetTypeKeys.optimalInventoryBySP:
              tempLegendHeight = (0.2 * finalHeight) - 5;
              finalHeight -= (0.2 * finalHeight);
              break;
            default:
              tempLegendHeight = 0.22 * finalHeight - 5;
              finalHeight -= 0.22 * finalHeight;
          }
          setLegendsHeight(tempLegendHeight);
        }
        setChartHeight(finalHeight);
      } else {
        setChartHeight(window.innerHeight);
      }
    };

    // Initial measurement
    updateDimensions();

    // Add resize listener
    const resizeObserver = new ResizeObserver(updateDimensions);
    resizeObserver.observe(containerNode);

    // Cleanup
    // eslint-disable-next-line consistent-return
    return () => {
      resizeObserver.disconnect();
    };
  }, [containerNode, legend, gridData]);

  const getSeriesConfig = () => {
    if (widgetID === widgetTypeKeys.optimalInventoryByStrategy) {
      return [{
        name: 'Optimal Inventory by Strategy',
        colorByPoint: true,
        data: data.map((item) => ({
          name: item.id || 'Unknown',
          y: Number(item.value || 0),
          count: Number(item.count || 0),
          inventory: Number(item.inventory || 0),
          color: resolvedColors?.[item.color] ?? item.color,
          drilldown: 'test',
        })),
      }];
    }

    if (widgetID === widgetTypeKeys.optimalInventoryByGroup
      || widgetID === widgetTypeKeys.optimalInventoryBySP) {
      const newData = data.map((item) => {
        const newItem = {};
        Object.keys(item).forEach((key) => {
          newItem[key.replace(/\s+/g, '_').toLowerCase()] = item[key];
        });
        return newItem;
      });

      return [
        {
          name: defaultTypes.APPROVED,
          data: newData.map((item) => ({
            y: Number(item.approved_stock || 0),
            count: Number(item.green_records || 0),
            inventory: Number(item.green_inventory || 0),
            color: defaultColorCodes.Green,
          })),
          color: defaultColorCodes.Green,
        },
        {
          name: defaultTypes.NOT_APPROVED,
          data: newData.map((item) => ({
            y: Number(item.not_approved_stock || 0),
            count: Number(item.red_records || 0),
            inventory: Number(item.red_inventory || 0),
            color: defaultColorCodes.Red,
          })),
          color: defaultColorCodes.Red,
        },
        {
          name: defaultTypes.UNKNOWN,
          data: newData.map((item) => ({
            y: Number(item.criticality_unknown || 0),
            count: Number(item.yellow_records || 0),
            inventory: Number(item.yellow_inventory || 0),
            color: defaultColorCodes.Yellow,
          })),
          color: defaultColorCodes.Yellow,
        },
      ].filter((series) => series.data.some((val) => val.y > 0));
    }

    if (widgetID === widgetTypeKeys.optimalInventoryByROP) {
      const newData = data.map((item) => {
        const newItem = {};
        Object.keys(item).forEach((key) => {
          newItem[key.replace(/\s+/g, '_').toLowerCase()] = item[key];
        });
        return newItem;
      });

      return [
        {
          name: defaultTypes.APPROVED,
          data: newData.map((item) => ({
            y: Number(item.approved_stock || 0),
            count: Number(item.green_records || 0),
            inventory: Number(item.green_inventory || 0),
            deviation: Number(item.rop_deviation_green || 0),
            color: defaultColorCodes.Green,
          })),
          color: defaultColorCodes.Green,
        },
        {
          name: defaultTypes.NOT_APPROVED,
          data: newData.map((item) => ({
            y: Number(item.not_approved_stock || 0),
            count: Number(item.red_records || 0),
            inventory: Number(item.red_inventory || 0),
            deviation: Number(item.rop_deviation_red || 0),
            color: defaultColorCodes.Red,
          })),
          color: defaultColorCodes.Red,
        },
      ].filter((series) => series.data.some((val) => val.y > 0));
    }

    if (widgetID === widgetTypeKeys.optimalInventoryByCrit) {
      const newData = data.map((item) => {
        const newItem = {};
        Object.keys(item).forEach((key) => {
          newItem[key.replace(/\s+/g, '_').toLowerCase()] = item[key];
        });
        return newItem;
      });

      return [
        {
          name: defaultTypes.APPROVED,
          data: newData.map((item) => ({
            y: Number(item.approved_stock || 0),
            count: Number(item.green_records || 0),
            inventory: Number(item.green_inventory || 0),
            deviation: Number(item.rop_deviation_green || 0),
            color: defaultColorCodes.Green,
          })),
          color: defaultColorCodes.Green,
        },
        {
          name: defaultTypes.NOT_APPROVED,
          data: newData.map((item) => ({
            y: Number(item.criticality_unknown || 0),
            count: Number(item.yellow_records || 0),
            inventory: Number(item.yellow_inventory || 0),
            deviation: Number(item.rop_deviation_yellow || 0),
            color: defaultColorCodes.Yellow,
          })),
          color: defaultColorCodes.Yellow,
        },
        {
          name: defaultTypes.UNKNOWN,
          data: newData.map((item) => ({
            y: Number(item.not_approved_stock || 0),
            count: Number(item.red_records || 0),
            inventory: Number(item.red_inventory || 0),
            deviation: Number(item.rop_deviation_red || 0),
            color: defaultColorCodes.Red,
          })),
          color: defaultColorCodes.Red,
        },
      ].filter((series) => series.data.some((val) => val.y > 0));
    }

    return [
      {
        name: defaultTypes.APPROVED,
        data: data.map((item) => ({
          y: Number(item.GREEN ? item.GREEN : item.GREEN_RECORDS || 0),
          count: Number(item.GREEN_RECORDS || 0),
          inventory: Number(item.GREEN_INVENTORY || 0),
          color: defaultColorCodes.Green,
        })),
        color: defaultColorCodes.Green,
      },
      {
        name: defaultTypes.NOT_APPROVED,
        data: data.map((item) => ({
          y: Number(item.RED ? item.RED : item.RED_RECORDS || 0),
          count: Number(item.RED_RECORDS || 0),
          inventory: Number(item.RED_INVENTORY || 0),
          color: defaultColorCodes.Red,
        })),
        color: defaultColorCodes.Red,
      },
      {
        name: defaultTypes.UNKNOWN,
        data: data.map((item) => ({
          y: Number(item.YELLOW ? item.YELLOW : item.YELLOW_RECORDS || 0),
          count: Number(item.YELLOW_RECORDS || 0),
          inventory: Number(item.YELLOW_INVENTORY || 0),
          color: defaultColorCodes.Yellow,
        })),
        color: defaultColorCodes.Yellow,
      },
    ].filter((series) => series.data.some((val) => val.y > 0));
  };

  const getRotation = () => {
    if (widgetID === widgetTypeKeys.optimalInventoryByStrategy) {
      let angle = 0;
      if (gridData?.colCount === 1) angle = -20;
      return data?.map(() => angle);
    }
    return data?.map(() => 0);
  };

  const hasLegends = () => {
    if (widgetID !== widgetTypeKeys.optimalInventoryByStrategy
      && widgetID !== widgetTypeKeys.excessStockByStatus) return true;
    return false;
  };

  const fontSizeForExport = {
    title: {
      style: {
        fontSize: 'max(1.2vw, calc((1.2 * 1.7778) * 1vh))',
        color: resolvedColors?.neutral100,
      }
    },
    labels: {
      style: {
        fontSize: 'max(0.7vw, calc((0.7 * 1.7778) * 1vh))',
        color: resolvedColors?.neutral100,
      },
    },
  };

  const options = {
    ...theme,
    exporting: {
      enabled: true,
      url: '',
      local: true,
      scale: 3,
      fallbackToExportServer: false,
      error: (_opt, err) => console.log(err),
      menuItemDefinitions: {
        viewData: {
          text: 'View data table'
        }
      },
      buttons: {
        contextButton: {
          menuItems: [
            'viewFullscreen',
            'printChart',
            'downloadPNG',
          ],
          symbol: 'menu',
          symbolX: 3,
          symbolFill: resolvedColors?.neutral100 || 'var(--neutral--100)',
          symbolStroke: resolvedColors?.neutral100 || 'var(--neutral--100)',
          symbolStrokeWidth: 1,
          theme: {
            fill: 'transparent',
            states: {
              hover: {
                fill: 'transparent',
              },
              select: {
                fill: 'transparent',
              }
            }
          }
        }
      },
      chartOptions: {
        chart: {
          spacingBottom: 0,
          backgroundColor: resolvedColors?.widgetBg,
        },
        yAxis: fontSizeForExport,
        xAxis: {
          labels: {
            ...fontSizeForExport.labels,
            useHTML: true,
            formatter() {
              // eslint-disable-next-line react/no-this-in-sfc
              return `<div style="font-size:max(1.2vw, calc((1.2 * 1.7778) * 1vh)) !important">${this.value}</div>`;
            }
          },
        },
        plotOptions: {
          column: {
            dataLabels: {
              enabled: true,
              style: fontSizeForExport.labels.style
            }
          }
        },
        legend: {
          enabled: hasLegends(),
          itemStyle: fontSizeForExport.labels.style,
          symbolRadius: 6,
          symbolHeight: 7,
          symbolPadding: 0,
        },
        colors: theme.colors,
        series: getSeriesConfig(),
      }
    },
    navigation: {
      buttonOptions: {
        align: 'right',
        verticalAlign: 'top',
        y: 20, // Move button up slightly
        x: 10, // Move button left slightly
        theme: {
          padding: 50
        }
      },
      menuStyle: {
        background: 'var(--neutral--800)',
        color: 'var(--neutral--100)'
      },
      menuItemStyle: {
        color: 'var(--neutral--100)',
        fontSize: '14px',
        padding: '0px 7px',
        transition: 'background 200ms',
      },
      menuItemHoverStyle: {
        background: 'var(--neutral--600)',
        color: 'var(--neutral--100)'
      }
    },
    legend: {
      enabled: hasLegends(),
      ...theme.legend,
      useHTML: true,
      align: 'center',
      verticalAlign: 'bottom',
      layout: 'horizontal',
      // y: 2,
      margin: 0,
      // margin: getCorrectLegendsMargin(),
      itemDistance: 35,
      formatter: (name) => {
        if (legend) return name;
        const matchingLegend = legendsList
          .flatMap((list) => list.data)
          .find((item) => item.id === name);
        return matchingLegend ? matchingLegend.label : name;
      },
      itemStyle: {
        cursor: 'pointer',
        fontSize: 'max(0.6vw, calc((0.6 * 1.7778) * 1vh))',
        color: resolvedColors?.neutral400 || 'var(--neutral--400)'
      },
      itemHoverStyle: {
        color: resolvedColors?.neutral100 || 'var(--neutral--100)'
      },
    },
    chart: {
      ...theme.chart,
      type: 'column',
      height: chartHeight,
      // spacingTop: 10,
      // marginTop: 10,
      animation: false,
      spacingBottom: hasLegends() ? 0 : 10,
      backgroundColor: 'var(--widget-background)',
      events: {
        load() {
          setAnimation({
            duration: 1200,
            easing: 'easeOutBounce',
            start: {
              y: 0
            }
          });
        },
        redraw() {
          const labels = document.getElementsByClassName(`x-axis-vertical-${widgetID}`);
          setTimeout(() => {
            Array.from(labels).forEach((x) => {
              // eslint-disable-next-line no-param-reassign
              const column = {
                seriesName: widgetID,
                index: widgetID,
                key: x.innerHTML,
              };
              // eslint-disable-next-line no-param-reassign
              x.onclick = () => {
                handleClick(column, true);
              };
            });
          }, 300);
        }
      }
    },
    title: {
      text: undefined,
    },
    tooltip: {
      ...theme.tooltip,
      useHTML: true,
      followPointer: true,
      backgroundColor: 'var(--widget-tooltip)',
      color: 'var(--tooltip-text-color)',
      borderColor: 'var(--neutral--600)',
      borderRadius: 4,
      padding: 0,
      style: {
        zIndex: 9999,
      },
      /* eslint-disable */
      formatter() {
        const point = {
          id: this.series.name,
          data: {
            count: this.point.count,
            inventory: this.point.inventory,
            color: this.point.color,
            y: this.y,
            ...this.point,
          },
        };
        let tooltipContent = `
          <div style="
            padding: 12px;
            background: var(--widget-tooltip);
            border-radius: 4px;
            color: var(--tooltip-text-color);
            font-size: max(0.6vw, calc((0.6 * 1.7778) * 1vh));"
          >
            <strong>Number of records:</strong> ${getNoOfRecords(point)}<br/>
          `;

        if (canViewValues && widgetID !== widgetTypeKeys.cleanUpWidget && widgetID !== widgetTypeKeys.inflowWatchlist) {
          if (widgetID === widgetTypeKeys.optimalInventoryByROP) {
            tooltipContent += `<strong>ROP deviation:</strong> ${getROPDeviation(point)}<br/>`;
            tooltipContent += `<strong>Sum of monetary impact:</strong> ${getInventoryValue(point)}`;
          } else {
            tooltipContent += `<strong>Value of inventory:</strong> ${getInventoryValue(point)}`;
          }
        }

        tooltipContent += '</div>';
        return tooltipContent;
      },
    },
    responsive: {
      rules: [{
        condition: {
          maxWidth: 500,
        },
        chartOptions: {
          legend: {
            align: 'center',
            verticalAlign: 'bottom',
            layout: 'horizontal',
          },
        },
      }],
    },
    xAxis: {
      ...theme.xAxis,
      categories: data.map((item) => item[indexBy] || item.GROUP),
      labels: {
        ...theme.xAxis?.labels,
        padding: 1,
        distance: 5,
        autoRotation: getRotation(),
        step: 1,
        useHTML: true,
        allowOverlap: false,
        style: {
          width: 'auto',
          staggerLines: 2,
          margin: 0,
          color: resolvedColors?.neutral400 || 'var(--neutral--400)',
          fontWeight: '400',
          overflow: 'hidden',
          cursor: 'pointer'
        },
        formatter() {
          return `<div
            style="width:max(3.3vw, calc((3.3 * 1.7778) * 1vh));
              white-space: 'normal';
              word-break:'normal';
              overflow-wrap:break-word;
              font-size:max(0.6vw, calc((0.6 * 1.7778) * 1vh));"
            class="x-axis-vertical-${widgetID}"
            >${this.value}</div>`;
        }
      },
    },
    yAxis: {
      ...theme.yAxis,
      type: scale?.type === scales.symlog.type ? 'logarithmic' : 'linear',
      title: {
        ...theme.yAxis?.title,
        text: getLegendLabel(),
        // offset: 130,
        style: {
          fontSize: 'max(0.6vw, calc((0.6 * 1.7778) * 1vh))',
          color: resolvedColors?.neutral400 || 'var(--neutral--400)',
          textOutline: 'none',
        },
      },
      labels: {
        ...theme.yAxis?.labels,
        allowOverlap: false,
        style: {
          color: resolvedColors?.neutral400 || 'var(--neutral--400)',
          fontWeight: '400',
          fontSize: 'max(0.6vw, calc((0.6 * 1.7778) * 1vh))',
        },
        formatter() {
          if (scale?.type === scales.symlog.type && this.value === 0.1) return '0';
          if (scale?.type === scales.symlog.type && this.value < 10) return '';
          if(widgetID !== widgetTypeKeys.cleanUpWidget && widgetID !== widgetTypeKeys.inflowWatchlist && this.value > 100) return '';
          if (this.value > maxValue * 5) return '';
          return getAxisLeftFormat(this.value);
        },
      },
      ceiling: getLegendLabel() === 'Percentage' ? 100 : undefined,
      min: scale?.type === scales.symlog.type ? 0.05 : 0,
      max: maxValue && Number(maxValue) < 100 ? maxValue : undefined,
      tickInterval: scale?.type === scales.symlog.type ? undefined : (() => {
        if (widgetID === widgetTypeKeys.cleanUpWidget || widgetID === widgetTypeKeys.inflowWatchlist) {
          const max = Number(maxValue) || 100;
          return Math.pow(10, Math.floor(Math.log10(max / 5)));
        }
        return 10;
      })(),
      tickAmount: scale?.type === scales.symlog.type ? 4 : undefined,
      minorTickInterval: scale?.type === scales.symlog.type ? 0.1 : 0,
      gridLineWidth: scale?.type === scales.symlog.type ? 0 : 1,
      minorGridLineWidth: scale?.type === scales.symlog.type ? 0 : 1,
      gridLines: {
        enabled: scale?.type !== scales.symlog.type
      },
      minorGridLines: {
        enabled: scale?.type !== scales.symlog.type
      },
      startOnTick: true,
      endOnTick: true,
      height: '100%',
      stackLabels: {
        borderColor: '#ff0000',
        enabled: true,
        crop: false,
        // overflow: 'allow',
        useHTML: true,
        formatter() {
          let { total } = this;
          if (Number.isNaN(total) || total === 0) total = '';
          else if (widgetID === widgetTypeKeys.cleanUpWidget || widgetID === widgetTypeKeys.inflowWatchlist) return `${total}`;
          else total = `${total?.toFixed(1)}%`;
          return total;
        },
        style: {
          fontWeight: '400',
          color: resolvedColors?.neutral100 || 'var(--neutral--100)',
          textOutline: 'none',
          cursor: 'pointer',
          fontSize: 'max(0.6vw, calc((0.6 * 1.7778) * 1vh))',
        },
      },
    },
    plotOptions: {
      column: {
        stacking: 'normal',
        groupPadding: 0,
        pointPadding: 0.1,
        opacity: 1,
        dataLabels: {
          enabled: isStackedChart,
          overflow: false,
          // crop: true,
          style: {
            color: resolvedColors?.neutral100 || 'var(--neutral--100)',
            fontWeight: '400',
            textOutline: 'none',
            fontSize: 'max(0.6vw, calc((0.6 * 1.7778) * 1vh))',
          },
          formatter() {
            // eslint-disable-next-line react/no-this-in-sfc
            const { point, series } = this;
            const { chart } = series;
            const xIndex = point.x;
            let nonZeroPoints = 0;
            chart.series.forEach((s) => {
              if (s.visible) {
                const p = s?.points?.[xIndex];
                if (p && p?.y > 0) {
                  nonZeroPoints += 1;
                }
              }
            });

            if (point.y === 0) {
              return '';
            }
            return getBarLabel({ value: point.y });
          },
        },
        borderWidth: 0,
      },
      series: {
        point: {
          events: {
            click: function (event) {
              const pointData = {
                seriesName: this.series.name,
                category: this.category,
                index: this.index,
                y: this.y,
                color: this.color,
                key: this.key,
                count: this.count,
                inventory: this.inventory,
                id: this.id,
                pointData: this.point,
              };
              handleClick(pointData);
            },
            mouseOver: function () {
              let hoveredPoint = this;
              hoveredPoint.series.chart.series.forEach(
                (s) => s?.data?.forEach((point) => {
                  if (point !== hoveredPoint) {
                    if (point?.color === hoveredPoint?.color) {
                      point?.graphic?.css({ opacity: 0.2 });
                      point?.dataLabel?.attr({ opacity: 0.2 });
                    } else {
                      point?.graphic?.css({ opacity: 0.7 });
                    }
                  }
                })
              );
            },
            mouseOut: function () {
              let hoveredPoint = this;
              hoveredPoint.series.chart.series.forEach(
                (s) => s?.data?.forEach((point) => {
                  point?.graphic?.css({ opacity: 1 });
                  point?.dataLabel?.attr({ opacity: 1 });
                })
              );
            },
          }
        },
        animation: widgetID === widgetTypeKeys.optimalInventoryByStrategy ? animationVal : animation
      }
    },
    colors: theme.colors,
    series: getSeriesConfig(),
    credits: {
      enabled: false,
    },
  };

  const getCorrectLegendListSize = () => {
    if(widgetID === widgetTypeKeys.excessStockByStatus || widgetID === widgetTypeKeys.optimalInventoryByStrategy) {
      return legendsList;
    }
    return legendsList.slice(1);
  }

  return (!inventoryStatusData
    || inventoryStatusData.length === 0
    || !data
    || _.isEmpty(inventoryStatusData))
    || (!_.isEmpty(dashboardFilter?.filterVals) && !hasDataAfterFilter(data))
    ? (
      <Space
        direction="vertical"
        style={{
          width: '100%', height: '100%', justifyContent: 'center', alignItems: 'center',
        }}
      >
        <Empty description={_.isEmpty(dashboardFilter?.filterVals) ? 'No data' : 'No data after filtering'} />
      </Space>
    )
    : (
      <div style={{ width: '100%', height: '100%' }}>
        <DraggableModal
          content={infoContent}
          handle=".handle"
          title={modalTitle}
          centered
          visible={infoModalVisible}
          style={{ maxWidth: '80vw' }}
          onCancel={closeInfoModal}
          footer={[
            <Button key="close" type="primary" onClick={closeInfoModal}>
              Close
            </Button>,
          ]}
        >
          { }
        </DraggableModal>
        <div className={styles.widgetButtons}>
          <span>
            {widgetID === widgetTypeKeys.optimalInventoryByGroup && (
              <Tooltip title="Consumption Group Info">
                <InfoCircleTwoTone onClick={showInfoStorageCheckID} style={{ marginRight: 5 }} />
              </Tooltip>
            )}
          </span>
          <Button onClick={toggleLegend} style={{
            padding: '0px 8px',
            fontSize: '12px',
            height: '25px',
            marginTop: '4px',
          }}>
            {legend ? 'Hide legend' : 'Show legend'}
          </Button>
          <Button
            onClick={updateValueScale}
            className="topRightButton"
            style={{
              padding: '0px 8px',
              fontSize: '12px',
              height: '25px',
              marginTop: '4px',
            }}
          >
            {scale?.type === scales.symlog.type ? 'Show linear scale' : 'Show log. scale'}
          </Button>
        </div>
        <div className={styles.fullChartContainer}>
          {
            textWidgetID === 'totalExcessStock' && canViewValues
            && widgetID === widgetTypeKeys.excessStockByStatus
              ? (
                <div className={styles.widgetText}>
                  <TextWidgetContainer
                    widgetID={textWidgetID}
                  />
                </div>
              )
              : <></>
          }
          <div ref={containerRef} className={styles.chartContainer}>
            <HighchartsReact
              highcharts={Highcharts}
              options={{
                ...options,
              }}
              ref={chartComponentRef}
            />
          </div>
          {legend && (
            <div className={styles.legendsContainer} style={{ height: `${legendsHeight}px` }}>
              {getCorrectLegendListSize().map((list) => {
                const legendData = list.data;

                return (
                  <div className={styles.legendRow} key={uuid()}>
                    {legendData.map((item, i) => {

                      if(item.label === '') return '';
                      const getColor = (color) => {
                        if(color.includes('green')) return '#00977B';
                        if(color.includes('red')) return '#E24973';
                        if(color.includes('yellow')) return '#E8D166';
                        return '#00977B';
                      }

                      const createClickItem = {
                        key: item.indexValue,
                        label: item.label,
                        seriesName: item.id,
                        color: getColor(item.color),
                      }
                      return (
                      <div className={styles.legendItem} key={uuid()} onClick={() => handleClick(createClickItem)}>
                        <div className={styles.legendColor} style={{ backgroundColor: item.color, borderRadius: '50%' }} />
                        <div className={styles.legendLabel}>{item.label}</div>
                      </div>
                    );
                    })}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
}

VerticalGraphWidget.defaultProps = {
  showLegendSetting: false,
  keysVisible: true,
  label: '',
  keys: null,
  textWidgetID: null,
};

VerticalGraphWidget.propTypes = {
  inventoryStatusData: PropTypes.arrayOf(PropTypes.any).isRequired,
  showLegendSetting: PropTypes.bool,
  canViewValues: PropTypes.bool.isRequired,
  types: PropTypes.objectOf(PropTypes.string).isRequired,
  colorMapping: PropTypes.objectOf(PropTypes.string).isRequired,
  keysVisible: PropTypes.bool,
  label: PropTypes.string,
  widgetID: PropTypes.string.isRequired,
  getFormattedData: PropTypes.func.isRequired,
  indexBy: PropTypes.string.isRequired,
  keys: PropTypes.arrayOf(PropTypes.string),
  textWidgetID: PropTypes.string,
  theme: PropTypes.objectOf(PropTypes.any).isRequired,
};
